/**
 * Phase Data Processor - Utility for processing scenario data into visualization-ready format
 * Handles extraction and transformation of EBIOS RM phase data for React Flow visualization
 */

/**
 * Extract and process phase-specific data from a scenario
 * @param {Object} scenario - The operational scenario
 * @param {string} selectedPhase - The phase to process (optional, processes all if not specified)
 * @returns {Object} Processed data with nodes and edges
 */
export const processPhaseData = (scenario, selectedPhase = null) => {
  if (!scenario?.operationalSteps) {
    return { nodes: [], edges: [], phases: [] };
  }

  const phases = scenario.operationalSteps.map(step => step.phase).filter(Boolean);

  if (selectedPhase) {
    const step = scenario.operationalSteps.find(s => s.phase === selectedPhase);
    return step ? processSinglePhase(step, scenario) : { nodes: [], edges: [], phases };
  }

  // Process all phases
  return processAllPhases(scenario.operationalSteps, scenario);
};

/**
 * Process a single phase into nodes and edges
 * @param {Object} step - The phase step data
 * @param {Object} scenario - The full scenario context
 * @returns {Object} Nodes and edges for the phase
 */
const processSinglePhase = (step, scenario) => {
  const nodes = [];
  const edges = [];
  let nodeId = 1;

  // Define layout constants for clean positioning
  const layout = {
    threatSource: { x: 50, y: 300 },
    phase: { x: 450, y: 300 },
    technique: { x: 850, y: 300 },
    tools: { x: 1200, y: 150 },
    indicators: { x: 1550, y: 150 },
    assets: { x: 850, y: 500 },
    vulnerabilities: { x: 450, y: 500 },
    spacing: {
      vertical: 120,
      horizontal: 200
    }
  };

  // Create threat source node (attacker) - positioned on the left
  const threatSourceNode = {
    id: 'threat-source',
    type: 'threatSource',
    position: layout.threatSource,
    data: {
      label: scenario.threatSource || 'Attaquant',
      threatType: scenario.threatType || 'cybercrime',
      capability: scenario.capability || 'Intermédiaire',
      motivation: scenario.motivation || 'Profit financier',
      description: 'Source de la menace qui initie l\'attaque'
    },
  };
  nodes.push(threatSourceNode);

  // Main phase node - positioned in center-left
  const phaseNode = {
    id: 'phase-main',
    type: 'phase',
    position: layout.phase,
    data: {
      phase: step.phase,
      technique: step.technique,
      description: step.description,
      duration: step.duration,
      difficulty: step.difficulty,
      stepProbability: step.stepProbability,
      stepNumber: step.stepNumber
    },
  };
  nodes.push(phaseNode);

  // Threat source initiates the phase
  edges.push(createEdge('threat-source', 'phase-main', 'initiates', getRelationshipColor('leads_to'), 'leads_to'));

  // Process technique - positioned in center-right
  let techniqueNode = null;
  if (step.technique) {
    techniqueNode = {
      id: `technique-${nodeId++}`,
      type: 'technique',
      position: layout.technique,
      data: {
        label: step.technique,
        description: 'Technique principale utilisée dans cette phase',
        mitreId: extractMitreId(step.technique)
      },
    };
    nodes.push(techniqueNode);
    // Phase employs technique
    edges.push(createEdge('phase-main', techniqueNode.id, 'employs', getRelationshipColor('uses'), 'uses'));
  }

  // Process indicators - positioned on the right side, vertically stacked
  const indicatorNodes = [];
  if (step.indicators && step.indicators.length > 0) {
    step.indicators.forEach((indicator, index) => {
      const indicatorData = typeof indicator === 'string'
        ? { label: indicator, description: null, severity: 'Medium' }
        : {
          label: indicator.indicator || indicator.name || `Indicateur ${index + 1}`,
          description: indicator.description,
          severity: indicator.severity || 'Medium'
        };

      const indicatorNode = {
        id: `indicator-${nodeId++}`,
        type: 'indicator',
        position: {
          x: layout.indicators.x,
          y: layout.indicators.y + (index * layout.spacing.vertical)
        },
        data: indicatorData,
      };
      nodes.push(indicatorNode);
      indicatorNodes.push(indicatorNode);

      // Logical connection: technique triggers indicator (not phase directly)
      if (techniqueNode) {
        edges.push(createEdge(techniqueNode.id, indicatorNode.id, 'triggers', getRelationshipColor('triggers'), 'triggers'));
      } else {
        // Fallback if no technique node
        edges.push(createEdge('phase-main', indicatorNode.id, 'triggers', getRelationshipColor('triggers'), 'triggers'));
      }
    });
  }

  // Process tools - positioned on the right side, vertically stacked above indicators
  const toolNodes = [];
  if (step.tools && step.tools.length > 0) {
    step.tools.forEach((tool, index) => {
      const toolData = typeof tool === 'string'
        ? { label: tool, description: null }
        : {
          label: tool.name || tool.tool || `Outil ${index + 1}`,
          description: tool.description
        };

      const toolNode = {
        id: `tool-${nodeId++}`,
        type: 'tool',
        position: {
          x: layout.tools.x,
          y: layout.tools.y + (index * layout.spacing.vertical)
        },
        data: toolData,
      };
      nodes.push(toolNode);
      toolNodes.push(toolNode);

      // Logical connection: technique uses tool (not phase directly)
      if (techniqueNode) {
        edges.push(createEdge(techniqueNode.id, toolNode.id, 'uses', getRelationshipColor('uses'), 'uses'));
      } else {
        // Fallback if no technique node
        edges.push(createEdge('phase-main', toolNode.id, 'uses', getRelationshipColor('uses'), 'uses'));
      }
    });
  }

  // Add related assets - positioned below technique as targets
  const assetNodes = [];
  if (step.assets && step.assets.length > 0) {
    step.assets.forEach((asset, index) => {
      const assetNode = {
        id: `asset-${nodeId++}`,
        type: 'asset',
        position: {
          x: layout.assets.x + (index * layout.spacing.horizontal),
          y: layout.assets.y
        },
        data: {
          label: asset,
          assetType: 'Unknown',
          criticality: 'Medium'
        },
      };
      nodes.push(assetNode);
      assetNodes.push(assetNode);

      // Logical connection: technique targets asset
      if (techniqueNode) {
        edges.push(createEdge(techniqueNode.id, assetNode.id, 'targets', getRelationshipColor('requires'), 'requires'));
      } else {
        edges.push(createEdge('phase-main', assetNode.id, 'targets', getRelationshipColor('requires'), 'requires'));
      }
    });
  }

  // Add vulnerabilities - positioned below phase as exploitable weaknesses
  if (step.vulnerabilities && step.vulnerabilities.length > 0) {
    step.vulnerabilities.forEach((vuln, index) => {
      const vulnNode = {
        id: `vuln-${nodeId++}`,
        type: 'vulnerability',
        position: {
          x: layout.vulnerabilities.x + (index * layout.spacing.horizontal),
          y: layout.vulnerabilities.y
        },
        data: {
          label: vuln,
          cveId: vuln,
          severity: 'Medium',
        },
      };
      nodes.push(vulnNode);

      // Logical connection: technique exploits vulnerability
      if (techniqueNode) {
        edges.push(createEdge(techniqueNode.id, vulnNode.id, 'exploits', getRelationshipColor('requires'), 'requires'));
      } else {
        edges.push(createEdge('phase-main', vulnNode.id, 'exploits', getRelationshipColor('requires'), 'requires'));
      }

      // Vulnerability affects assets
      if (assetNodes.length > 0) {
        assetNodes.forEach(assetNode => {
          edges.push(createEdge(vulnNode.id, assetNode.id, 'affects', getRelationshipColor('detects'), 'detects'));
        });
      }
    });
  }

  // Create logical cross-connections: tools generate indicators that can be detected
  if (toolNodes.length > 0 && indicatorNodes.length > 0) {
    // Only connect tools to indicators if they're related (limit to avoid clutter)
    toolNodes.forEach((toolNode, toolIndex) => {
      // Connect each tool to corresponding indicator (if exists)
      if (indicatorNodes[toolIndex]) {
        edges.push(createEdge(
          toolNode.id,
          indicatorNodes[toolIndex].id,
          'generates',
          getRelationshipColor('triggers'),
          'triggers'
        ));
      }
    });
  }

  return { nodes, edges, phases: [step.phase] };
};

/**
 * Process all phases into a comprehensive visualization
 * @param {Array} steps - All operational steps
 * @param {Object} scenario - The full scenario context
 * @returns {Object} Nodes and edges for all phases
 */
const processAllPhases = (steps, scenario) => {
  const nodes = [];
  const edges = [];
  let nodeId = 1;

  const phases = steps.map(step => step.phase).filter(Boolean);
  const phaseSpacing = 400;

  // Create phase nodes
  steps.forEach((step, index) => {
    if (!step.phase) return;

    const phaseNode = {
      id: `phase-${index}`,
      type: 'phase',
      position: { x: index * phaseSpacing + 200, y: 100 },
      data: {
        phase: step.phase,
        technique: step.technique,
        description: step.description,
        duration: step.duration,
        difficulty: step.difficulty,
        stepProbability: step.stepProbability,
        stepNumber: step.stepNumber
      },
    };
    nodes.push(phaseNode);

    // Connect phases in sequence
    if (index > 0) {
      edges.push(createEdge(`phase-${index - 1}`, `phase-${index}`, 'puis', '#6b7280'));
    }

    // Add technique for each phase
    if (step.technique) {
      const techniqueNode = {
        id: `technique-${index}`,
        type: 'technique',
        position: { x: index * phaseSpacing + 100, y: 250 },
        data: {
          label: step.technique,
          description: `Technique pour ${step.phase}`,
          mitreId: extractMitreId(step.technique)
        },
      };
      nodes.push(techniqueNode);
      edges.push(createEdge(`phase-${index}`, techniqueNode.id, 'utilise', '#6366f1'));
    }

    // Add key indicators (limit to 2 per phase for clarity)
    if (step.indicators && step.indicators.length > 0) {
      step.indicators.slice(0, 2).forEach((indicator, indIndex) => {
        const indicatorData = typeof indicator === 'string'
          ? { label: indicator, severity: 'Medium' }
          : {
            label: indicator.indicator || indicator.name || `Indicateur ${indIndex + 1}`,
            description: indicator.description,
            severity: indicator.severity || 'Medium'
          };

        const indicatorNode = {
          id: `indicator-${index}-${indIndex}`,
          type: 'indicator',
          position: { x: index * phaseSpacing + 300 + (indIndex * 120), y: 250 },
          data: indicatorData,
        };
        nodes.push(indicatorNode);
        // Create intelligent connection: phase triggers indicator
        edges.push(createEdge(`phase-${index}`, indicatorNode.id, 'triggers', getRelationshipColor('triggers'), 'triggers'));
      });
    }

    // Add key tools (limit to 2 per phase for clarity) with intelligent connections
    if (step.tools && step.tools.length > 0) {
      step.tools.slice(0, 2).forEach((tool, toolIndex) => {
        const toolData = typeof tool === 'string'
          ? { label: tool }
          : {
            label: tool.name || tool.tool || `Outil ${toolIndex + 1}`,
            description: tool.description
          };

        const toolNode = {
          id: `tool-${index}-${toolIndex}`,
          type: 'tool',
          position: { x: index * phaseSpacing + 100 + (toolIndex * 120), y: 400 },
          data: toolData,
        };
        nodes.push(toolNode);

        // Create intelligent connection: phase uses tool
        edges.push(createEdge(`phase-${index}`, toolNode.id, 'uses', getRelationshipColor('uses'), 'uses'));
      });
    }
  });

  return { nodes, edges, phases };
};

/**
 * Create an edge object for React Flow
 * @param {string} source - Source node ID
 * @param {string} target - Target node ID
 * @param {string} label - Edge label
 * @param {string} color - Edge color
 * @returns {Object} Edge object
 */
const createEdge = (source, target, label, color, relationship = 'uses') => ({
  id: `edge-${source}-${target}`,
  source,
  target,
  type: 'smoothstep',
  animated: true,
  label,
  style: { stroke: color, strokeWidth: 2 },
  markerEnd: { type: 'arrowclosed', color },
  labelStyle: { fontSize: 12, fontWeight: 500 },
  labelBgStyle: { fill: 'white', fillOpacity: 0.8 },
  data: { relationship, description: label }
});

/**
 * Get color based on relationship type
 * @param {string} relationship - The relationship type
 * @returns {string} Color hex code
 */
const getRelationshipColor = (relationship) => {
  const colors = {
    'uses': '#3b82f6',      // Blue - technique uses tool
    'enables': '#10b981',   // Green - tool enables technique
    'detects': '#f59e0b',   // Orange - indicator detects technique
    'requires': '#ef4444',  // Red - technique requires tool
    'leads_to': '#8b5cf6',  // Purple - technique leads to another
    'triggers': '#ec4899'   // Pink - technique triggers indicator
  };
  return colors[relationship] || '#6b7280'; // Default gray
};

/**
 * Extract MITRE ATT&CK ID from technique name
 * @param {string} technique - Technique name
 * @returns {string|null} MITRE ID if found
 */
const extractMitreId = (technique) => {
  if (!technique) return null;

  // Look for patterns like T1234, T1234.001, etc.
  const mitrePattern = /T\d{4}(?:\.\d{3})?/;
  const match = technique.match(mitrePattern);
  return match ? match[0] : null;
};

/**
 * Calculate optimal layout positions for nodes
 * @param {Array} nodes - Array of nodes
 * @param {string} layoutType - Layout type ('hierarchical', 'circular', 'force')
 * @returns {Array} Nodes with updated positions
 */
export const calculateLayout = (nodes, layoutType = 'hierarchical') => {
  switch (layoutType) {
    case 'circular':
      return calculateCircularLayout(nodes);
    case 'force':
      return calculateForceLayout(nodes);
    case 'hierarchical':
    default:
      return calculateHierarchicalLayout(nodes);
  }
};

/**
 * Calculate hierarchical layout positions
 * @param {Array} nodes - Array of nodes
 * @returns {Array} Nodes with updated positions
 */
const calculateHierarchicalLayout = (nodes) => {
  const levels = {};
  const nodesByType = {};

  // Group nodes by type
  nodes.forEach(node => {
    if (!nodesByType[node.type]) {
      nodesByType[node.type] = [];
    }
    nodesByType[node.type].push(node);
  });

  // Define level hierarchy
  const typeHierarchy = {
    phase: 0,
    technique: 1,
    indicator: 1,
    tool: 2,
    asset: 2,
    vulnerability: 3
  };

  // Position nodes by hierarchy
  Object.keys(nodesByType).forEach(type => {
    const level = typeHierarchy[type] || 0;
    const nodesInType = nodesByType[type];

    nodesInType.forEach((node, index) => {
      const spacing = 200;
      const levelY = level * 150 + 100;
      const totalWidth = (nodesInType.length - 1) * spacing;
      const startX = -totalWidth / 2 + 400; // Center around x=400

      node.position = {
        x: startX + (index * spacing),
        y: levelY
      };
    });
  });

  return nodes;
};

/**
 * Calculate circular layout positions
 * @param {Array} nodes - Array of nodes
 * @returns {Array} Nodes with updated positions
 */
const calculateCircularLayout = (nodes) => {
  const centerX = 400;
  const centerY = 300;
  const radius = 200;

  nodes.forEach((node, index) => {
    const angle = (2 * Math.PI * index) / nodes.length;
    node.position = {
      x: centerX + radius * Math.cos(angle),
      y: centerY + radius * Math.sin(angle)
    };
  });

  return nodes;
};

/**
 * Calculate force-directed layout positions (simplified)
 * @param {Array} nodes - Array of nodes
 * @returns {Array} Nodes with updated positions
 */
const calculateForceLayout = (nodes) => {
  // Simple grid layout as a placeholder for force-directed
  const cols = Math.ceil(Math.sqrt(nodes.length));
  const spacing = 200;

  nodes.forEach((node, index) => {
    const row = Math.floor(index / cols);
    const col = index % cols;

    node.position = {
      x: col * spacing + 100,
      y: row * spacing + 100
    };
  });

  return nodes;
};

/**
 * Filter and enhance scenario data for visualization
 * @param {Object} scenario - The scenario to process
 * @param {Object} options - Processing options
 * @returns {Object} Enhanced scenario data
 */
export const enhanceScenarioData = (scenario, options = {}) => {
  const {
    includeAssets = true,
    includeVulnerabilities = true,
    maxIndicatorsPerPhase = 3,
    maxToolsPerPhase = 3
  } = options;

  if (!scenario) return null;

  const enhanced = { ...scenario };

  // Enhance operational steps
  if (enhanced.operationalSteps) {
    enhanced.operationalSteps = enhanced.operationalSteps.map(step => ({
      ...step,
      indicators: step.indicators?.slice(0, maxIndicatorsPerPhase) || [],
      tools: step.tools?.slice(0, maxToolsPerPhase) || [],
    }));
  }

  // Filter assets if needed
  if (!includeAssets) {
    delete enhanced.businessAssets;
    delete enhanced.supportAssets;
  }

  // Filter vulnerabilities if needed
  if (!includeVulnerabilities) {
    delete enhanced.vulnerabilities;
  }

  return enhanced;
};

export default {
  processPhaseData,
  calculateLayout,
  enhanceScenarioData
};
